import { useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';
import type { StoredCharacter } from '../../utils/characterDatabase';
import { readJsonFile, importCharacters, isCatalogExport, isSingleCharacterExport } from '../../utils/catalogExport';

interface ImportLibraryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: (importedCount: number) => void;
  existingCharacters: StoredCharacter[];
}

type ImportState = 'idle' | 'reading' | 'previewing' | 'importing' | 'complete' | 'error';

interface PreviewData {
  type: 'single' | 'catalog';
  characterCount: number;
  characterNames: string[];
}

/**
 * Dialog for importing characters from a JSON file.
 * Supports both single character exports and full catalog exports.
 */
export function ImportLibraryDialog({
  isOpen,
  onClose,
  onImportComplete,
  existingCharacters,
}: ImportLibraryDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [state, setState] = useState<ImportState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [parsedData, setParsedData] = useState<unknown>(null);
  const [importResult, setImportResult] = useState<{
    imported: number;
    skipped: number;
    errors: string[];
  } | null>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setState('idle');
      setError(null);
      setPreview(null);
      setParsedData(null);
      setImportResult(null);
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setState('reading');
    setError(null);

    try {
      const data = await readJsonFile(file);

      // Determine file type and create preview
      if (isCatalogExport(data)) {
        setPreview({
          type: 'catalog',
          characterCount: data.characterCount,
          characterNames: data.characters.slice(0, 5).map((c) => c.draft?.name || 'Unnamed'),
        });
        setParsedData(data);
        setState('previewing');
      } else if (isSingleCharacterExport(data)) {
        setPreview({
          type: 'single',
          characterCount: 1,
          characterNames: [data.draft?.name || 'Unnamed Character'],
        });
        setParsedData(data);
        setState('previewing');
      } else {
        throw new Error('Unrecognized file format. Please select a valid character or library JSON file.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to read file';
      setError(message);
      setState('error');
    }

    // Reset file input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImport = async () => {
    if (!parsedData) return;

    setState('importing');
    setError(null);

    try {
      const result = await importCharacters(parsedData, existingCharacters);

      setImportResult({
        imported: result.importedCount,
        skipped: result.skippedCount,
        errors: result.errors,
      });

      if (result.success) {
        setState('complete');
        onImportComplete(result.importedCount);
      } else {
        setError(result.errors[0] || 'Import failed');
        setState('error');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Import failed';
      setError(message);
      setState('error');
    }
  };

  const handleClose = () => {
    if (state === 'importing') return; // Don't close while importing
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div
          ref={dialogRef}
          className={clsx(
            'w-full max-w-md bg-slate-800 rounded-xl border border-slate-700',
            'shadow-2xl'
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="import-dialog-title"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-700">
            <h2 id="import-dialog-title" className="text-lg font-semibold text-white">
              Import Characters
            </h2>
            <button
              onClick={handleClose}
              disabled={state === 'importing'}
              className="p-1 text-slate-400 hover:text-slate-300 rounded disabled:opacity-50"
              aria-label="Close dialog"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {/* Idle / Select File State */}
            {(state === 'idle' || state === 'error') && (
              <div className="space-y-4">
                <p className="text-sm text-slate-400">
                  Select a character JSON file or a library export to import. Imported characters will be added to your library (existing characters won't be replaced).
                </p>

                <div className="flex flex-col items-center gap-4 p-6 border-2 border-dashed border-slate-600 rounded-lg">
                  <UploadIcon className="h-10 w-10 text-slate-500" />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json,application/json"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="import-file-input"
                  />
                  <label
                    htmlFor="import-file-input"
                    className="px-4 py-2 rounded-lg bg-amber-600 text-white font-medium cursor-pointer hover:bg-amber-700 transition-colors"
                  >
                    Select JSON File
                  </label>
                  <p className="text-xs text-slate-500">
                    Accepts single character or library exports
                  </p>
                </div>

                {error && (
                  <div className="p-3 rounded-lg bg-red-900/20 border border-red-500/30">
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                )}
              </div>
            )}

            {/* Reading State */}
            {state === 'reading' && (
              <div className="flex flex-col items-center gap-4 py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-amber-500 border-t-transparent" />
                <p className="text-slate-400">Reading file...</p>
              </div>
            )}

            {/* Preview State */}
            {state === 'previewing' && preview && (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-slate-700/50">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{preview.type === 'catalog' ? '📚' : '📄'}</span>
                    <span className="font-medium text-white">
                      {preview.type === 'catalog' ? 'Library Export' : 'Single Character'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-400 mb-3">
                    {preview.characterCount} character{preview.characterCount !== 1 ? 's' : ''} found
                  </p>
                  <div className="space-y-1">
                    {preview.characterNames.map((name, i) => (
                      <div key={i} className="text-sm text-slate-300 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        {name}
                      </div>
                    ))}
                    {preview.characterCount > 5 && (
                      <p className="text-xs text-slate-500 mt-2">
                        ...and {preview.characterCount - 5} more
                      </p>
                    )}
                  </div>
                </div>

                <p className="text-sm text-slate-400">
                  Characters will be added to your library. If a character with the same name exists, it will be renamed with "(Imported)" suffix.
                </p>
              </div>
            )}

            {/* Importing State */}
            {state === 'importing' && (
              <div className="flex flex-col items-center gap-4 py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-amber-500 border-t-transparent" />
                <p className="text-slate-400">Importing characters...</p>
              </div>
            )}

            {/* Complete State */}
            {state === 'complete' && importResult && (
              <div className="space-y-4">
                <div className="flex flex-col items-center gap-3 py-4">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CheckIcon className="h-6 w-6 text-green-400" />
                  </div>
                  <p className="text-lg font-medium text-white">Import Complete!</p>
                  <p className="text-slate-400">
                    {importResult.imported} character{importResult.imported !== 1 ? 's' : ''} imported
                    {importResult.skipped > 0 && `, ${importResult.skipped} skipped`}
                  </p>
                </div>

                {importResult.errors.length > 0 && (
                  <div className="p-3 rounded-lg bg-yellow-900/20 border border-yellow-500/30">
                    <p className="text-sm font-medium text-yellow-400 mb-1">Warnings:</p>
                    <ul className="text-xs text-yellow-300/80 list-disc list-inside">
                      {importResult.errors.slice(0, 3).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 pb-6 flex gap-3">
            {(state === 'idle' || state === 'error') && (
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-2.5 rounded-lg border border-slate-600 text-slate-300 font-medium hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
            )}

            {state === 'previewing' && (
              <>
                <button
                  onClick={() => {
                    setState('idle');
                    setPreview(null);
                    setParsedData(null);
                  }}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-slate-600 text-slate-300 font-medium hover:bg-slate-700 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleImport}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-amber-600 text-white font-medium hover:bg-amber-700 transition-colors"
                >
                  Import {preview?.characterCount} Character{preview?.characterCount !== 1 ? 's' : ''}
                </button>
              </>
            )}

            {state === 'complete' && (
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-2.5 rounded-lg bg-amber-600 text-white font-medium hover:bg-amber-700 transition-colors"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}
